/**
 * MetricStrip：一张卡内并排 2–6 个指标（评审 `docs/archive/reviews/2026-09-05-ui-premium-review-codex.md`
 * §5.1：六个独立 `Metric` 由业务 grid 排布，没有共同容器）。子项 props 与 `Metric` 对齐
 * （label/value/suffix/detail/tone/icon，tone 上色规则复用 `metricValueToneClasses`），
 * 新增 `href`/`onSelect` 让「工作台三组计数能直达」（11-ui-kit §10.1）可以直接落地：
 * 与 AppShell 导航项同一惯例——href 走整页跳转，onSelect 走 preventDefault 后调用（SPA 导航）。
 *
 * 发丝线分隔用 CSS Grid 的「gap 当分隔线」写法：容器背景=分隔线色、`gap-px`、子项自己刷面板色，
 * 露出的 1px 缝隙天然就是发丝线——不管在哪一列折行都对，不需要按列数反推 nth-child。
 *
 * 六个以上请拆成两条 MetricStrip（超过 6 个会抛错，见下方运行期契约）。
 */
import type { MouseEvent, ReactNode } from 'react'
import { metricValueToneClasses } from './Metric'
import type { Tone } from './tones'

export type MetricStripItem = {
  /** 稳定唯一键（列表 key，不参与展示） */
  key: string
  label: ReactNode
  value: ReactNode
  suffix?: ReactNode
  detail?: ReactNode
  tone?: Tone
  icon?: ReactNode
  /** 整页跳转（静态目标/无 SPA 路由时）；与 onSelect 同给时 onSelect 优先（同 AppShell 导航项惯例） */
  href?: string
  /** SPA 导航：点击先 preventDefault 再调用 */
  onSelect?: () => void
}

export type MetricStripProps = {
  items: MetricStripItem[]
  /** 整条 strip 的可达名称（role="group" 的 aria-label） */
  ariaLabel?: string
}

/**
 * 响应式列数（工单§2.1）：≥1280 单行 N 列；<1280 两行（列数 = ceil(N/2)）；<640 每行两个。
 * N 固定在 2–6，逐一枚举成完整字面量——Tailwind 只扫描源码里出现的完整类名，运行时拼接的
 * `sm:grid-cols-${n}` 这类字符串它看不见（同类先例见 `tones.ts` 顶部注释）。
 */
const COLS_CLASSES: Record<number, string> = {
  2: 'grid-cols-2 sm:grid-cols-1 xl:grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-2 xl:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-2 xl:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-5',
  6: 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-6'
}

function MetricStripCell({ item }: { item: MetricStripItem }) {
  const tone = item.tone ?? 'neutral'
  const interactive = Boolean(item.href || item.onSelect)
  const className = `block min-w-0 bg-[var(--color-panel)] px-4 py-3 text-left no-underline motion-safe:transition-colors focus:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[var(--color-focus)]${
    interactive ? ' cursor-pointer hover:bg-[var(--color-soft)]' : ''
  }`
  const content = (
    <>
      <p className="m-0 flex items-center gap-2 text-xs font-medium text-[var(--color-ink-secondary)]">
        {item.icon ? <span className="flex size-5 shrink-0 items-center justify-center [&>svg]:size-5">{item.icon}</span> : null}
        <span className="min-w-0 truncate">{item.label}</span>
      </p>
      <p className={`m-0 mt-1 font-[family-name:var(--font-num)] text-[length:var(--text-metric)] leading-tight font-semibold tracking-[-0.02em] tabular-nums [overflow-wrap:anywhere] ${metricValueToneClasses[tone]}`}>
        {item.value}
        {item.suffix ? <span className="ml-1 font-sans text-sm font-medium tracking-normal text-[var(--color-ink-secondary)]">{item.suffix}</span> : null}
      </p>
      {item.detail ? <p className="m-0 mt-1 text-[length:var(--text-caption)] leading-5 text-[var(--color-ink-secondary)] [overflow-wrap:anywhere]">{item.detail}</p> : null}
    </>
  )
  const onClick = item.onSelect
    ? (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        event.preventDefault()
        item.onSelect!()
      }
    : undefined
  if (item.href) {
    return (
      <a href={item.href} className={className} onClick={onClick}>
        {content}
      </a>
    )
  }
  if (item.onSelect) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {content}
      </button>
    )
  }
  return <div className={className}>{content}</div>
}

export function MetricStrip({ items, ariaLabel }: MetricStripProps) {
  if (items.length < 2 || items.length > 6) {
    // MetricStrip 是共享容器组件，子项数量是它唯一的结构契约：少于 2 个该用单张 `Metric`，
    // 多于 6 个请拆成两条 MetricStrip（README/11-ui-kit §9.3 有此提示，这里落成运行期硬断言，
    // 不做静默截断——截断会让「三组计数能直达」悄悄少一组而没人发现）。
    throw new Error(`MetricStrip 需要 2–6 个指标，收到 ${items.length} 个；超过 6 个请拆成两条 MetricStrip（11-ui-kit.md §9.3）。`)
  }
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`grid gap-px overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-line)] bg-[var(--color-line-soft)] shadow-[var(--shadow-card)] ${COLS_CLASSES[items.length]}`}
    >
      {items.map((item) => (
        <MetricStripCell key={item.key} item={item} />
      ))}
    </div>
  )
}
